/// <reference types="google.maps" />
import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';

declare global {
  interface Window {
    google: typeof google;
    __googleMapsCallback?: () => void;
  }
}

let loadPromise: Promise<void> | null = null;

export function useGoogleMapsLoader() {
  const [isLoaded, setIsLoaded] = useState(!!window.google?.maps);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (window.google?.maps) {
      setIsLoaded(true);
      return;
    }

    if (loadPromise) {
      await loadPromise;
      setIsLoaded(true);
      return;
    }

    loadPromise = (async () => {
      // Fetch API key from edge function (admin-only)
      const { data, error: fnError } = await supabase.functions.invoke('get-maps-config');
      if (fnError || !data?.apiKey) {
        throw new Error(fnError?.message || 'Failed to get Maps API key');
      }

      return new Promise<void>((resolve, reject) => {
        const script = document.createElement('script');
        script.src = `https://maps.googleapis.com/maps/api/js?key=${data.apiKey}&libraries=places`;
        script.async = true;
        script.defer = true;
        script.onload = () => resolve();
        script.onerror = () => reject(new Error('Failed to load Google Maps script'));
        document.head.appendChild(script);
      });
    })();

    try {
      await loadPromise;
      setIsLoaded(true);
    } catch (e: any) {
      setError(e.message);
      loadPromise = null;
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  return { isLoaded, error };
}
